/**
 * <s-product-slideshow>
 * Product page media slideshow: one slide at a time with native snap scrolling (swipe on touch),
 * previous/next arrows, a thumbnail strip, a "1 / n" counter and arrow-key support.
 * When a variant with its own featured media is selected, it moves to that slide.
 *
 * Markup contract (templates/product.liquid):
 *   [data-slideshow-track]  horizontally scrolling element; each direct child [data-slide] is one full-width slide
 *   [data-slide][data-media-id]
 *   [data-slideshow-prev] / [data-slideshow-next]  buttons
 *   [data-slideshow-thumb][data-index]  thumbnail buttons
 *   [data-slideshow-counter]  live "1 / n" text
 *   [data-variant-media-id]  (inside the product-form partial) featured media id of the selected variant
 */
class ProductSlideshow extends HTMLElement {
  #index = 0;
  #frame = 0;
  #observer = null;
  #resizeObserver = null;
  #variantMediaId = null;
  #programmatic = false;
  #settleFrame = 0;

  connectedCallback() {
    this.track = this.querySelector('[data-slideshow-track]');
    if (!this.track) return;
    this.slides = [...this.track.querySelectorAll(':scope > [data-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-slideshow-thumb]')];
    this.prev = this.querySelector('[data-slideshow-prev]');
    this.next = this.querySelector('[data-slideshow-next]');
    this.counter = this.querySelector('[data-slideshow-counter]');
    if (this.slides.length < 2) return;

    this.track.addEventListener('scroll', this.#onScroll, { passive: true });
    this.prev?.addEventListener('click', () => this.goTo(this.#index - 1));
    this.next?.addEventListener('click', () => this.goTo(this.#index + 1));
    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => this.goTo(Number(thumb.dataset.index)));
    });
    this.addEventListener('keydown', this.#onKeydown);

    this.#variantMediaId = this.#readVariantMediaId();
    const form = document.querySelector('[data-product-form]');
    if (form) {
      this.#observer = new MutationObserver(() => this.#onFormChange());
      this.#observer.observe(form, { childList: true, subtree: true });
    }

    this.#update();
    // Open on the selected variant only when the link names one (?variant=); otherwise start on the main image.
    if (new URL(location.href).searchParams.has('variant')) this.#syncToVariant(true);

    // Keep the current slide aligned when the gallery changes width.
    this.#resizeObserver = new ResizeObserver(() => {
      const offset = Math.abs(this.track.scrollLeft) - this.#index * this.track.clientWidth;
      if (Math.abs(offset) > 1) this.goTo(this.#index, { instant: true });
    });
    this.#resizeObserver.observe(this.track);
  }

  disconnectedCallback() {
    this.track?.removeEventListener('scroll', this.#onScroll);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#observer?.disconnect();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
    cancelAnimationFrame(this.#settleFrame);
  }

  get #isRtl() {
    return getComputedStyle(this.track).direction === 'rtl';
  }

  goTo(index, { instant = false } = {}) {
    const count = this.slides.length;
    const target = Math.max(0, Math.min(count - 1, index));
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const left = target * this.track.clientWidth * (this.#isRtl ? -1 : 1);

    // Make sure the destination photo loads even though it starts off-screen.
    this.slides[target]?.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });

    // Slides use snap-stop so a swipe moves one photo at a time. Mobile browsers apply that to scripted
    // scrolls too and halt one slide along, so snapping is paused until the jump reaches its target.
    this.#programmatic = true;
    this.track.style.scrollSnapType = 'none';
    this.track.scrollTo({ left, behavior: instant || reduceMotion ? 'auto' : 'smooth' });
    this.#index = target;
    this.#update();
    this.#settle(left);
  }

  #settle(left) {
    cancelAnimationFrame(this.#settleFrame);
    const started = performance.now();
    const check = () => {
      const arrived = Math.abs(this.track.scrollLeft - left) <= 1;
      if (arrived || performance.now() - started > 1200) {
        if (!arrived) this.track.scrollTo({ left, behavior: 'auto' });
        this.track.style.scrollSnapType = '';
        this.#programmatic = false;
        return;
      }
      this.#settleFrame = requestAnimationFrame(check);
    };
    this.#settleFrame = requestAnimationFrame(check);
  }

  #onScroll = () => {
    if (this.#programmatic) return;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.track.clientWidth || 1;
      const index = Math.round(Math.abs(this.track.scrollLeft) / width);
      if (index !== this.#index) {
        this.#index = index;
        this.#update();
      }
    });
  };

  #onKeydown = (event) => {
    if (event.target.closest('input, select, textarea')) return;
    const forward = this.#isRtl ? 'ArrowLeft' : 'ArrowRight';
    const back = this.#isRtl ? 'ArrowRight' : 'ArrowLeft';
    if (event.key === forward) {
      event.preventDefault();
      this.goTo(this.#index + 1);
    } else if (event.key === back) {
      event.preventDefault();
      this.goTo(this.#index - 1);
    }
  };

  #readVariantMediaId() {
    return document.querySelector('[data-variant-media-id]')?.dataset.variantMediaId || null;
  }

  #onFormChange() {
    // The product form re-renders for reasons other than a variant change; only move when the variant image changes.
    const mediaId = this.#readVariantMediaId();
    if (!mediaId || mediaId === this.#variantMediaId) return;
    this.#variantMediaId = mediaId;
    this.#syncToVariant();
  }

  #syncToVariant(instant = false) {
    const mediaId = document.querySelector('[data-variant-media-id]')?.dataset.variantMediaId;
    if (!mediaId) return;
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === mediaId);
    if (index >= 0 && index !== this.#index) this.goTo(index, { instant });
  }

  #update() {
    const count = this.slides.length;
    if (this.prev) this.prev.disabled = this.#index <= 0;
    if (this.next) this.next.disabled = this.#index >= count - 1;
    if (this.counter) this.counter.textContent = `${this.#index + 1} / ${count}`;
    this.slides.forEach((slide, i) => {
      slide.inert = i !== this.#index;
    });
    this.thumbs.forEach((thumb, i) => {
      const active = i === this.#index;
      thumb.toggleAttribute('aria-current', active);
      if (active) {
        const strip = thumb.parentElement?.parentElement;
        if (strip && strip.scrollWidth > strip.clientWidth) {
          const offset = thumb.offsetLeft - strip.clientWidth / 2 + thumb.clientWidth / 2;
          strip.scrollTo({ left: offset, behavior: 'auto' });
        }
      }
    });
  }
}

if (!customElements.get('s-product-slideshow')) {
  customElements.define('s-product-slideshow', ProductSlideshow);
}
